// src/pages/customer-panel/chat/ChatPage.js
// Customer Panel - Full Chat Interface with Sidebar, General/Work Bench/People tabs, Citations

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import axiosInstance from '../../../utils/axiosInstance';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton,
    Checkbox,
    Switch,
    Avatar,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    Button,
    Tooltip,
    InputAdornment,
    Drawer,
    Badge,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Radio,
    RadioGroup,
    FormControlLabel,
    Rating,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import LanguageIcon from '@mui/icons-material/Language';
import DescriptionIcon from '@mui/icons-material/Description';
import MicIcon from '@mui/icons-material/Mic';
import EmailIcon from '@mui/icons-material/Email';
import TuneIcon from '@mui/icons-material/Tune';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SecurityIcon from '@mui/icons-material/Security';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import StarIcon from '@mui/icons-material/Star';
import { liveChatSocket } from '../../../utils/LiveChatSocket';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';
const GREEN_LIGHT = '#d1fae5';
const GREEN_DARK = '#059669';
const SIDEBAR_WIDTH = 280;
const CITATIONS_WIDTH = 300;

export default function ChatPage() {
    const navigate = useNavigate();
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || 'customer';
    const isAdminOrEmployee = userRole === 'admin' || userRole === 'employee';
    const userName = user?.profile?.fname || 'You';

    // --- Layout State ---
    const [sidebarOpen, setSidebarOpen] = useState(false); // mobile sidebar
    const [citationsOpen, setCitationsOpen] = useState(false); // mobile citations

    // --- Chatbot Config ---
    const [chatbotConfigured, setChatbotConfigured] = useState(null);
    const [chatSettings, setChatSettings] = useState(null);

    // --- Tab State ---
    const [activeTab, setActiveTab] = useState(0);

    // --- Conversation Sidebar State ---
    const [conversations, setConversations] = useState([]);
    const [loadingConversations, setLoadingConversations] = useState(false);
    const [currentSessionCode, setCurrentSessionCode] = useState(null);
    const [editingConvId, setEditingConvId] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const [sidebarSearch, setSidebarSearch] = useState('');

    // --- General Chat State ---
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [chatError, setChatError] = useState('');
    const messagesEndRef = useRef(null);

    // --- Citations State ---
    const [citations, setCitations] = useState([]);

    // --- Personalize (Work Bench) State ---
    const [dataSources, setDataSources] = useState([]);
    const [selectedSources, setSelectedSources] = useState([]);
    const [loadingSources, setLoadingSources] = useState(false);
    const [sourcesError, setSourcesError] = useState('');
    const [isPersonalizeConfigured, setIsPersonalizeConfigured] = useState(false);
    const [personalizeMessages, setPersonalizeMessages] = useState([]);
    const [personalizeInput, setPersonalizeInput] = useState('');
    const [personalizeSending, setPersonalizeSending] = useState(false);
    const [personalizeSessionCode, setPersonalizeSessionCode] = useState(null);
    const [personalizePrivacy, setPersonalizePrivacy] = useState(true);
    const personalizeEndRef = useRef(null);

    // --- Live Agent Chat State ---
    const [liveSessionId, setLiveSessionId] = useState(null);
    const [showEscalationPrompt, setShowEscalationPrompt] = useState(false);
    const [isAgentTyping, setIsAgentTyping] = useState(false);
    const [currentAgent, setCurrentAgent] = useState(null);
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [sessionToRateId, setSessionToRateId] = useState(null);
    const [ratingValue, setRatingValue] = useState(0);
    const [ratingHover, setRatingHover] = useState(-1);
    const [ratingComment, setRatingComment] = useState('');
    const [ratingSubmitted, setRatingSubmitted] = useState(false);
    const [ratingSubmitting, setRatingSubmitting] = useState(false);
    const [endingChat, setEndingChat] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [feedbackIssue, setFeedbackIssue] = useState('');
    const [feedbackComments, setFeedbackComments] = useState('');
    const agentTypingTimerRef = useRef(null);
    const lastSentTypingRef = useRef(0);

    // --- People State ---
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [loadingTeam, setLoadingTeam] = useState(false);
    const [teamError, setTeamError] = useState('');
    const [isPeopleConfigured, setIsPeopleConfigured] = useState(false);
    const [peopleMessages, setPeopleMessages] = useState([]);
    const [peopleInput, setPeopleInput] = useState('');
    const [peopleSending, setPeopleSending] = useState(false);
    const [peopleSessionCode, setPeopleSessionCode] = useState(null);
    const [peoplePrivacy, setPeoplePrivacy] = useState(true);
    const peopleEndRef = useRef(null);

    // =========================
    // INITIALIZATION
    // =========================

    useEffect(() => {
        axiosInstance.get('/chat/initial-data')
            .then(res => {
                if (res.data?.success) {
                    const data = res.data.data;
                    setChatSettings(data.settings);
                    setChatbotConfigured(true);
                } else {
                    setChatbotConfigured(false);
                }
            })
            .catch(() => setChatbotConfigured(false));
    }, []);

    // Fetch conversations on mount
    useEffect(() => {
        if (chatbotConfigured) fetchConversations();
    }, [chatbotConfigured]);

    // Auto-scroll
    const scrollToBottom = useCallback((ref) => {
        ref?.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => { scrollToBottom(messagesEndRef); }, [messages, scrollToBottom]);
    useEffect(() => { scrollToBottom(personalizeEndRef); }, [personalizeMessages, scrollToBottom]);
    useEffect(() => { scrollToBottom(peopleEndRef); }, [peopleMessages, scrollToBottom]);

    // =========================
    // LIVE AGENT CHAT
    // =========================

    const handleChatEnd = useCallback((sessionId) => {
        setSessionToRateId(sessionId);
        setShowRatingModal(true);
        setRatingValue(0);
        setRatingHover(-1);
        setRatingComment('');
        setRatingSubmitted(false);
        setLiveSessionId(null);
        setCurrentAgent(null);
        setIsAgentTyping(false);
        setEndingChat(false);
        if (agentTypingTimerRef.current) clearTimeout(agentTypingTimerRef.current);
    }, []);

    // WebSocket message handler
    useEffect(() => {
        if (!liveSessionId || !user?.user_id) return;

        const handleMessage = (data) => {
            if (data.session_id !== liveSessionId) return;

            if (data.type === 'typing_indicator') {
                if (data.sender_id !== user.user_id) {
                    setIsAgentTyping(true);
                    if (agentTypingTimerRef.current) clearTimeout(agentTypingTimerRef.current);
                    agentTypingTimerRef.current = setTimeout(() => setIsAgentTyping(false), 3000);
                }
                return;
            }

            if (data.type === 'message') {
                if (data.sender_id === user.user_id) return;
                setIsAgentTyping(false);
                if (agentTypingTimerRef.current) clearTimeout(agentTypingTimerRef.current);
                setMessages(prev => [...prev, {
                    id: `agent-${Date.now()}`,
                    is_from_user: false,
                    message: data.message,
                    senderType: 'agent',
                    created_at: new Date().toISOString(),
                }]);
            } else if (data.type === 'system') {
                if (data.message === 'agent_resolved_chat') {
                    setEndingChat(true);
                    setMessages(prev => [...prev, {
                        id: `system-end-${Date.now()}`,
                        is_from_user: false,
                        message: 'The agent has ended this chat session.',
                        senderType: 'system',
                        created_at: new Date().toISOString(),
                    }]);
                    setTimeout(() => handleChatEnd(data.session_id), 1500);
                } else if (data.event === 'agent_assigned' && data.profile) {
                    setCurrentAgent(data.profile);
                    setMessages(prev => [...prev, {
                        id: `system-agent-${Date.now()}`,
                        is_from_user: false,
                        message: `You are now chatting with ${data.profile.name} (${data.profile.role}).`,
                        senderType: 'system',
                        created_at: new Date().toISOString(),
                    }]);
                }
            }
        };

        setShowEscalationPrompt(false);
        liveChatSocket.connect(liveSessionId, user.user_id, handleMessage);
        setMessages(prev => [...prev, {
            id: `system-connected-${Date.now()}`,
            is_from_user: false,
            message: "You've been connected to the live support queue. An agent will be with you shortly.",
            senderType: 'system',
            created_at: new Date().toISOString(),
        }]);

        return () => {
            if (agentTypingTimerRef.current) clearTimeout(agentTypingTimerRef.current);
            liveChatSocket.disconnect(liveSessionId, handleMessage);
        };
    }, [liveSessionId, user?.user_id, handleChatEnd]);

    const handleRequestAgent = async () => {
        setSending(true);
        setShowEscalationPrompt(false);
        try {
            const res = await axiosInstance.post('/live-chat/request');
            if (res.data?.success) {
                setLiveSessionId(res.data.data.session_id);
            } else {
                setChatError(res.data?.message || 'Could not request an agent.');
            }
        } catch (err) {
            setChatError(err.response?.data?.message || 'Could not request an agent at this time.');
        } finally {
            setSending(false);
        }
    };

    const handleEndLiveChat = async () => {
        if (!liveSessionId || endingChat) return;
        const endedSessionId = liveSessionId;
        setEndingChat(true);
        try {
            await axiosInstance.post(`/live-chat/sessions/${endedSessionId}/close`);
            setMessages(prev => [...prev, {
                id: `system-end-${Date.now()}`,
                is_from_user: false,
                message: 'You have ended the live chat session.',
                senderType: 'system',
                created_at: new Date().toISOString(),
            }]);
            handleChatEnd(endedSessionId);
        } catch (err) {
            setEndingChat(false);
            setChatError(err.response?.data?.message || 'Could not end the session.');
        }
    };

    const handleRatingSubmit = async () => {
        if (!sessionToRateId || ratingValue === 0) return;
        setRatingSubmitting(true);
        try {
            await axiosInstance.post(`/live-chat/sessions/${sessionToRateId}/feedback`, {
                rating: ratingValue,
                comment: ratingComment,
            });
            setRatingSubmitted(true);
        } catch (err) {
            setChatError(err.response?.data?.message || 'Could not submit feedback.');
        } finally {
            setRatingSubmitting(false);
        }
    };

    const handleFeedbackSubmit = async () => {
        if (!feedbackIssue || !feedbackMessage) return;
        try {
            await axiosInstance.post('/chat/feedback', {
                ai_message_id: feedbackMessage.id,
                session_code: feedbackMessage.session_code,
                session_code_number: feedbackMessage.session_code_number,
                user_issue: feedbackIssue,
                user_feedback: feedbackComments,
            });
            setShowFeedbackModal(false);
            setFeedbackMessage(null);
            setFeedbackIssue('');
            setFeedbackComments('');
        } catch (err) {
            setChatError(err.response?.data?.message || 'Could not submit feedback.');
        }
    };

    const handleInputChange = (val) => {
        setInputMessage(val);
        if (liveSessionId && user?.user_id) {
            const now = Date.now();
            if (now - lastSentTypingRef.current > 2000) {
                liveChatSocket.sendTypingStart(liveSessionId, user.user_id);
                lastSentTypingRef.current = now;
            }
        }
    };

    // Fetch data sources / team when tabs activate
    useEffect(() => {
        if (activeTab === 1 && isAdminOrEmployee && dataSources.length === 0) fetchDataSources();
        if (activeTab === 2 && isAdminOrEmployee && teamMembers.length === 0) fetchTeamMembers();
    }, [activeTab]);

    // Clear citations on tab change
    useEffect(() => {
        setCitations([]);
        setCitationsOpen(false);
    }, [activeTab]);

    // =========================
    // CONVERSATION SIDEBAR
    // =========================

    const fetchConversations = async () => {
        setLoadingConversations(true);
        try {
            const res = await axiosInstance.get('/chat/conversations');
            setConversations(res.data?.data || []);
        } catch (err) {
            console.error('Error fetching conversations:', err);
        } finally {
            setLoadingConversations(false);
        }
    };

    const handleNewChat = () => {
        setCurrentSessionCode(null);
        setMessages([]);
        setChatError('');
        setCitations([]);
        setActiveTab(0);
        // Reset live agent state
        if (liveSessionId) {
            liveChatSocket.disconnect(liveSessionId);
        }
        setLiveSessionId(null);
        setEndingChat(false);
        setShowEscalationPrompt(false);
        setIsAgentTyping(false);
        setCurrentAgent(null);
        // Reset personalize/people
        setIsPersonalizeConfigured(false);
        setPersonalizeMessages([]);
        setPersonalizeSessionCode(null);
        setSelectedSources([]);
        setIsPeopleConfigured(false);
        setPeopleMessages([]);
        setPeopleSessionCode(null);
        setSelectedMembers([]);
        setSidebarOpen(false);
    };

    const handleSelectConversation = async (sessionCode) => {
        setCurrentSessionCode(sessionCode);
        setActiveTab(0);
        setCitations([]);
        setSidebarOpen(false);
        setLoadingHistory(true);
        setChatError('');
        try {
            const res = await axiosInstance.get(`/chat/initial-data?session_code=${sessionCode}`);
            if (res.data?.success) {
                setMessages(res.data.data.history || []);
            }
        } catch (err) {
            console.error('Error loading conversation:', err);
            setChatError('Failed to load conversation.');
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleDeleteConversation = async (sessionCode, e) => {
        e.stopPropagation();
        if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
        try {
            await axiosInstance.delete(`/chat/conversations/${sessionCode}`);
            setConversations(prev => prev.filter(c => c.session_code !== sessionCode));
            if (currentSessionCode === sessionCode) handleNewChat();
        } catch (err) {
            console.error('Error deleting conversation:', err);
        }
    };

    const startEditing = (conv, e) => {
        e.stopPropagation();
        setEditingConvId(conv.session_code);
        setEditTitle(conv.title || '');
    };

    const handleRenameConversation = async (sessionCode) => {
        if (!editTitle.trim()) return;
        try {
            await axiosInstance.patch(`/chat/conversations/${sessionCode}/title`, { title: editTitle.trim() });
            setConversations(prev => prev.map(c =>
                c.session_code === sessionCode ? { ...c, title: editTitle.trim() } : c
            ));
            setEditingConvId(null);
        } catch (err) {
            console.error('Error renaming conversation:', err);
        }
    };

    const formatTimestamp = (ts) => {
        if (!ts) return '';
        const date = new Date(ts);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const filteredConversations = conversations.filter(c =>
        !sidebarSearch || (c.title || '').toLowerCase().includes(sidebarSearch.toLowerCase())
    );

    // =========================
    // GENERAL CHAT
    // =========================

    const sendMessage = async () => {
        const trimmed = inputMessage.trim();
        if (!trimmed || sending) return;

        const userMsg = {
            id: `user-${Date.now()}`,
            is_from_user: true,
            message: trimmed,
            created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, userMsg]);
        setInputMessage('');

        // If in live agent session, send via WebSocket
        if (liveSessionId && user?.user_id) {
            liveChatSocket.sendMessage(liveSessionId, user.user_id, trimmed);
            return;
        }

        setSending(true);
        setChatError('');

        try {
            const payload = {
                message: trimmed,
                language_code: 'en-US',
                session_code: currentSessionCode,
            };
            const res = await axiosInstance.post('/chat/send-message', payload);
            if (res.data?.success) {
                const aiData = res.data.data;
                setMessages(prev => [...prev, aiData]);
                // Update session code if new conversation
                if (!currentSessionCode && aiData.session_code) {
                    setCurrentSessionCode(aiData.session_code);
                    fetchConversations(); // Refresh sidebar
                }
                // Check for escalation trigger
                if (aiData.should_escalate || (aiData.confidence_score && aiData.confidence_score < 0.6)) {
                    setShowEscalationPrompt(true);
                }
            } else {
                setChatError(res.data?.message || 'Failed to get response.');
            }
        } catch (err) {
            console.error('Error sending message:', err);
            setChatError(err.response?.data?.message || 'Failed to send message.');
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // =========================
    // PERSONALIZE (WORK BENCH)
    // =========================

    const fetchDataSources = async () => {
        setLoadingSources(true);
        setSourcesError('');
        try {
            const res = await axiosInstance.get('/personalize/data-sources');
            setDataSources(res.data?.data || []);
        } catch (err) {
            console.error('Error fetching data sources:', err);
            setSourcesError('Failed to load data sources.');
        } finally {
            setLoadingSources(false);
        }
    };

    const getSourceKey = (type, id) => `${type}-${id}`;

    const toggleSource = (type, id) => {
        const key = getSourceKey(type, id);
        setSelectedSources(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const toggleAllByType = (type) => {
        const typeSources = dataSources.filter(s => s.type === type && s.is_trained);
        const typeKeys = typeSources.map(s => getSourceKey(s.type, s.id));
        const allSelected = typeKeys.every(k => selectedSources.includes(k));
        if (allSelected) {
            setSelectedSources(prev => prev.filter(k => !typeKeys.includes(k)));
        } else {
            setSelectedSources(prev => [...new Set([...prev, ...typeKeys])]);
        }
    };

    const startPersonalizeChat = () => {
        if (selectedSources.length === 0) return;
        setIsPersonalizeConfigured(true);
        setPersonalizeMessages([]);
        setPersonalizeSessionCode(null);
    };

    const sendPersonalizeMessage = async () => {
        const trimmed = personalizeInput.trim();
        if (!trimmed || personalizeSending) return;

        const userMsg = {
            id: `user-${Date.now()}`,
            is_from_user: true,
            message: trimmed,
            created_at: new Date().toISOString(),
        };
        setPersonalizeMessages(prev => [...prev, userMsg]);
        setPersonalizeInput('');
        setPersonalizeSending(true);

        try {
            const res = await axiosInstance.post('/chat/personalize', {
                message: trimmed,
                source_ids: selectedSources,
                session_code: personalizeSessionCode,
                privacy: personalizePrivacy,
            });
            if (res.data?.success) {
                const aiData = res.data.data;
                setPersonalizeMessages(prev => [...prev, aiData]);
                if (!personalizeSessionCode && aiData.session_code) {
                    setPersonalizeSessionCode(aiData.session_code);
                }
            }
        } catch (err) {
            console.error('Error in personalize chat:', err);
            setPersonalizeMessages(prev => [...prev, {
                id: `err-${Date.now()}`,
                is_from_user: false,
                message: 'Sorry, an error occurred. Please try again.',
                created_at: new Date().toISOString(),
            }]);
        } finally {
            setPersonalizeSending(false);
        }
    };

    // =========================
    // PEOPLE CHAT
    // =========================

    const fetchTeamMembers = async () => {
        setLoadingTeam(true);
        setTeamError('');
        try {
            const res = await axiosInstance.get('/people/team-members');
            setTeamMembers(res.data?.data || []);
        } catch (err) {
            console.error('Error fetching team members:', err);
            setTeamError('Failed to load team members.');
        } finally {
            setLoadingTeam(false);
        }
    };

    const toggleMember = (userId) => {
        setSelectedMembers(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const startPeopleChat = () => {
        if (selectedMembers.length === 0) return;
        setIsPeopleConfigured(true);
        setPeopleMessages([]);
        setPeopleSessionCode(null);
    };

    const sendPeopleMessage = async () => {
        const trimmed = peopleInput.trim();
        if (!trimmed || peopleSending) return;

        const userMsg = {
            id: `user-${Date.now()}`,
            is_from_user: true,
            message: trimmed,
            created_at: new Date().toISOString(),
        };
        setPeopleMessages(prev => [...prev, userMsg]);
        setPeopleInput('');
        setPeopleSending(true);

        try {
            const res = await axiosInstance.post('/chat/people', {
                message: trimmed,
                user_ids: selectedMembers,
                session_code: peopleSessionCode,
                privacy: peoplePrivacy,
            });
            if (res.data?.success) {
                const aiData = res.data.data;
                setPeopleMessages(prev => [...prev, aiData]);
                if (!peopleSessionCode && aiData.session_code) {
                    setPeopleSessionCode(aiData.session_code);
                }
            }
        } catch (err) {
            console.error('Error in people chat:', err);
            setPeopleMessages(prev => [...prev, {
                id: `err-${Date.now()}`,
                is_from_user: false,
                message: 'Sorry, an error occurred. Please try again.',
                created_at: new Date().toISOString(),
            }]);
        } finally {
            setPeopleSending(false);
        }
    };

    // =========================
    // CITATIONS
    // =========================

    const handleShowCitations = (msg) => {
        if (msg.citations && msg.citations.length > 0) {
            setCitations(msg.citations);
            setCitationsOpen(true);
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
    };

    // =========================
    // SOURCE GROUP CONFIG
    // =========================

    const sourceTypeConfig = {
        website: { label: 'Websites', icon: <LanguageIcon /> },
        document: { label: 'Documents', icon: <DescriptionIcon /> },
        voice: { label: 'Voice Recordings', icon: <MicIcon /> },
        email: { label: 'Emails', icon: <EmailIcon /> },
    };

    const groupSourcesByType = () => {
        const groups = {};
        dataSources.forEach(source => {
            const type = source.type || 'document';
            if (!groups[type]) groups[type] = [];
            groups[type].push(source);
        });
        return groups;
    };

    // =========================
    // RENDER: Message Bubble (shared)
    // =========================

    const renderMessageBubble = (msg, index) => {
        const isUser = msg.is_from_user || msg.role === 'user' || msg.sender === 'user';
        const content = msg.message || msg.content || msg.text || '';
        const hasCitations = msg.citations && msg.citations.length > 0;
        const isAgent = msg.senderType === 'agent';
        const isSystem = msg.senderType === 'system';

        // System messages render as centered info chips
        if (isSystem) {
            return (
                <Box key={msg.id || index} sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                    <Chip
                        label={content}
                        size="small"
                        sx={{ fontFamily, fontSize: '0.75rem', bgcolor: '#f1f5f9', color: '#64748b', maxWidth: '80%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                    />
                </Box>
            );
        }

        return (
            <Box key={msg.id || index} sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', gap: 1, alignItems: 'flex-start' }}>
                {!isUser && (
                    <Avatar sx={{ bgcolor: isAgent ? '#6366f1' : (chatSettings?.primary_color || GREEN_ACCENT), width: 32, height: 32, mt: 0.5 }}>
                        {isAgent ? <SupportAgentIcon sx={{ fontSize: 18 }} /> : <SmartToyIcon sx={{ fontSize: 18 }} />}
                    </Avatar>
                )}
                <Box sx={{ maxWidth: '70%' }}>
                    {/* Agent name label */}
                    {isAgent && currentAgent && (
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#6366f1', mb: 0.25, ml: 0.5 }}>
                            {currentAgent.name}
                        </Typography>
                    )}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 1.5, px: 2,
                            borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                            bgcolor: isUser ? GREEN_ACCENT : '#ffffff',
                            color: isUser ? '#ffffff' : '#1e293b',
                            border: isUser ? 'none' : '1px solid #e2e8f0',
                            '& p': { margin: 0, fontFamily, fontSize: '0.875rem', lineHeight: 1.6 },
                            '& code': { bgcolor: isUser ? 'rgba(255,255,255,0.15)' : '#f1f5f9', px: 0.5, borderRadius: '4px', fontSize: '0.8rem' },
                            '& pre': { bgcolor: isUser ? 'rgba(0,0,0,0.15)' : '#f1f5f9', p: 1, borderRadius: '8px', overflow: 'auto' },
                            '& ul, & ol': { pl: 2, fontFamily, fontSize: '0.875rem' },
                        }}
                    >
                        <ReactMarkdown
                            rehypePlugins={[rehypeRaw]}
                            components={{
                                a: ({ node, ...props }) => (
                                    <a {...props} target="_blank" rel="noopener noreferrer" style={{ color: isUser ? '#e0f2fe' : '#2563eb', textDecoration: 'underline' }} />
                                ),
                            }}
                        >{content}</ReactMarkdown>
                        {msg.created_at && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7, fontFamily, fontSize: '0.7rem', textAlign: isUser ? 'right' : 'left', color: isUser ? 'rgba(255,255,255,0.8)' : '#94a3b8' }}>
                                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                        )}
                    </Paper>
                    {/* Action buttons for AI messages (not for agent messages) */}
                    {!isUser && !isAgent && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            <Tooltip title="Copy">
                                <IconButton size="small" onClick={() => handleCopy(content)} sx={{ color: '#94a3b8', '&:hover': { color: '#64748b' } }}>
                                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                            {hasCitations && (
                                <Tooltip title="View Sources">
                                    <IconButton size="small" onClick={() => handleShowCitations(msg)} sx={{ color: '#94a3b8', '&:hover': { color: GREEN_ACCENT } }}>
                                        <ArticleIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Feedback">
                                <IconButton size="small" onClick={() => { setFeedbackMessage(msg); setShowFeedbackModal(true); setFeedbackIssue(''); setFeedbackComments(''); }} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                    <ThumbDownOffAltIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
                {isUser && (
                    <Avatar sx={{ bgcolor: '#6366f1', width: 32, height: 32, mt: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                )}
            </Box>
        );
    };

    // =========================
    // RENDER: Chat Input (shared)
    // =========================

    const renderChatInput = (value, onChange, onSend, isSending, placeholder = 'Type your message...') => {
        const handleKey = (e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
        };
        return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                    fullWidth multiline maxRows={4}
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={handleKey}
                    disabled={isSending}
                    variant="outlined"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px', fontFamily, fontSize: '0.9rem',
                            '&.Mui-focused fieldset': { borderColor: GREEN_ACCENT },
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={onSend} disabled={isSending || !value.trim()}
                                    sx={{ bgcolor: GREEN_ACCENT, color: '#fff', '&:hover': { bgcolor: GREEN_DARK }, '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' }, width: 36, height: 36 }}>
                                    {isSending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 18 }} />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
        );
    };

    // =========================
    // RENDER: Empty Chat State
    // =========================

    const renderEmptyState = (icon, title, subtitle) => (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            {icon}
            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '1.1rem', mt: 2 }}>{title}</Typography>
            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.875rem', mt: 0.5 }}>{subtitle}</Typography>
        </Box>
    );

    // =========================
    // RENDER: Conversation Sidebar
    // =========================

    const renderSidebar = () => (
        <Box sx={{ width: SIDEBAR_WIDTH, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc', borderRight: '1px solid #e2e8f0' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Chats</Typography>
                    <Tooltip title="New Chat">
                        <IconButton size="small" onClick={handleNewChat} sx={{ bgcolor: GREEN_ACCENT, color: '#fff', '&:hover': { bgcolor: GREEN_DARK }, width: 30, height: 30 }}>
                            <AddIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <TextField
                    fullWidth size="small" placeholder="Search conversations..."
                    value={sidebarSearch} onChange={(e) => setSidebarSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily, fontSize: '0.8rem', bgcolor: '#fff' } }}
                />
            </Box>

            {/* Conversations List */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
                {loadingConversations ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} sx={{ color: GREEN_ACCENT }} /></Box>
                ) : filteredConversations.length === 0 ? (
                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.8rem', textAlign: 'center', py: 4 }}>No conversations yet</Typography>
                ) : (
                    filteredConversations.map(conv => (
                        <Box
                            key={conv.session_code}
                            onClick={() => handleSelectConversation(conv.session_code)}
                            sx={{
                                p: 1.5, mb: 0.5, borderRadius: '10px', cursor: 'pointer',
                                bgcolor: currentSessionCode === conv.session_code ? GREEN_LIGHT : 'transparent',
                                border: currentSessionCode === conv.session_code ? `1px solid ${GREEN_ACCENT}` : '1px solid transparent',
                                '&:hover': { bgcolor: currentSessionCode === conv.session_code ? GREEN_LIGHT : '#f1f5f9' },
                                '&:hover .conv-actions': { opacity: 1 },
                            }}
                        >
                            {editingConvId === conv.session_code ? (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                    <TextField size="small" fullWidth value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleRenameConversation(conv.session_code); if (e.key === 'Escape') setEditingConvId(null); }}
                                        autoFocus sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', fontFamily, fontSize: '0.8rem' } }}
                                    />
                                    <IconButton size="small" onClick={() => handleRenameConversation(conv.session_code)}><CheckIcon sx={{ fontSize: 16 }} /></IconButton>
                                    <IconButton size="small" onClick={() => setEditingConvId(null)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ChatIcon sx={{ fontSize: 16, color: currentSessionCode === conv.session_code ? GREEN_ACCENT : '#94a3b8' }} />
                                        <Typography noWrap sx={{ fontFamily, fontSize: '0.825rem', fontWeight: 500, color: '#1e293b', flex: 1 }}>
                                            {conv.title || 'New Conversation'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>
                                            {formatTimestamp(conv.last_activity)}
                                        </Typography>
                                        <Box className="conv-actions" sx={{ opacity: 0, display: 'flex', gap: 0.25, transition: 'opacity 0.2s' }}>
                                            <IconButton size="small" onClick={(e) => startEditing(conv, e)} sx={{ p: 0.25 }}>
                                                <EditIcon sx={{ fontSize: 14, color: '#64748b' }} />
                                            </IconButton>
                                            <IconButton size="small" onClick={(e) => handleDeleteConversation(conv.session_code, e)} sx={{ p: 0.25 }}>
                                                <DeleteOutlineIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Box>
                    ))
                )}
            </Box>

            {/* User Footer */}
            <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: GREEN_ACCENT, width: 32, height: 32, fontSize: '0.8rem', fontFamily, fontWeight: 600 }}>
                    {userName.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>{userName}</Typography>
                    <Typography noWrap sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', textTransform: 'capitalize' }}>{userRole}</Typography>
                </Box>
            </Box>
        </Box>
    );

    // =========================
    // RENDER: General Tab
    // =========================

    const renderGeneralTab = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {chatError && <Alert severity="error" sx={{ mb: 1, fontFamily }} onClose={() => setChatError('')}>{chatError}</Alert>}

            {/* Live Chat Header Banner */}
            {(liveSessionId || endingChat) && (
                <Paper elevation={0} sx={{ p: 1.5, px: 2, mb: 1, borderRadius: '10px', bgcolor: endingChat ? '#fef2f2' : '#eef2ff', border: `1px solid ${endingChat ? '#fecaca' : '#c7d2fe'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {endingChat ? (
                            <CircularProgress size={18} sx={{ color: '#ef4444' }} />
                        ) : (
                            <SupportAgentIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                        )}
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: endingChat ? '#dc2626' : '#4338ca' }}>
                            {endingChat ? 'Ending chat session...' : (currentAgent ? `${currentAgent.name} (${currentAgent.role})` : 'Live Agent Chat')}
                        </Typography>
                    </Box>
                    <Button size="small" onClick={handleEndLiveChat} disabled={endingChat}
                        sx={{ fontFamily, fontSize: '0.75rem', textTransform: 'none', color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' }, '&.Mui-disabled': { color: '#fca5a5' } }}>
                        {endingChat ? <CircularProgress size={14} sx={{ color: '#fca5a5' }} /> : 'End Chat'}
                    </Button>
                </Paper>
            )}

            {/* Messages Area */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, bgcolor: '#f8fafc', borderRadius: '12px', mb: 2 }}>
                {loadingHistory ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>
                ) : messages.length === 0 ? (
                    renderEmptyState(
                        <SmartToyIcon sx={{ fontSize: 56, color: '#cbd5e1' }} />,
                        chatSettings?.chatbot_name ? `Welcome to ${chatSettings.chatbot_name}` : 'Start a Conversation',
                        chatSettings?.greeting_text || 'Ask questions and get AI-powered responses'
                    )
                ) : (
                    messages.map((msg, idx) => renderMessageBubble(msg, idx))
                )}
                {/* AI typing indicator */}
                {sending && !liveSessionId && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: chatSettings?.primary_color || GREEN_ACCENT, width: 32, height: 32 }}>
                            <SmartToyIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Paper elevation={0} sx={{ p: 1.5, px: 2, borderRadius: '16px 16px 16px 4px', border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {[0, 1, 2].map(i => (
                                    <Box key={i} sx={{
                                        width: 8, height: 8, borderRadius: '50%', bgcolor: '#94a3b8',
                                        animation: 'bounce 1.4s infinite ease-in-out both',
                                        animationDelay: `${i * 0.16}s`,
                                        '@keyframes bounce': { '0%, 80%, 100%': { transform: 'scale(0)' }, '40%': { transform: 'scale(1)' } },
                                    }} />
                                ))}
                            </Box>
                        </Paper>
                    </Box>
                )}
                {/* Agent typing indicator */}
                {isAgentTyping && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: '#6366f1', width: 32, height: 32 }}>
                            <SupportAgentIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Paper elevation={0} sx={{ p: 1.5, px: 2, borderRadius: '16px 16px 16px 4px', border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                {[0, 1, 2].map(i => (
                                    <Box key={i} sx={{
                                        width: 8, height: 8, borderRadius: '50%', bgcolor: '#6366f1',
                                        animation: 'bounce 1.4s infinite ease-in-out both',
                                        animationDelay: `${i * 0.16}s`,
                                        '@keyframes bounce': { '0%, 80%, 100%': { transform: 'scale(0)' }, '40%': { transform: 'scale(1)' } },
                                    }} />
                                ))}
                            </Box>
                        </Paper>
                    </Box>
                )}
                <div ref={messagesEndRef} />
            </Box>

            {/* Escalation Prompt */}
            {showEscalationPrompt && (
                <Paper elevation={0} sx={{ p: 2, mb: 1, borderRadius: '12px', border: '1px solid #fbbf24', bgcolor: '#fffbeb', textAlign: 'center' }}>
                    <Typography sx={{ fontFamily, fontSize: '0.875rem', color: '#92400e', mb: 1.5 }}>
                        It looks like I'm having trouble providing the help you need. Would you like to talk to a live agent?
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
                        <Button variant="contained" size="small" onClick={handleRequestAgent}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '8px', bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK } }}>
                            Yes, connect me
                        </Button>
                        <Button variant="outlined" size="small" onClick={() => setShowEscalationPrompt(false)}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '8px', borderColor: '#d1d5db', color: '#6b7280', '&:hover': { bgcolor: '#f9fafb' } }}>
                            No, thanks
                        </Button>
                    </Box>
                </Paper>
            )}

            {renderChatInput(inputMessage, handleInputChange, sendMessage, sending || endingChat, endingChat ? 'Chat session ending...' : (liveSessionId ? 'Message the live agent...' : 'Type your message...'))}
        </Box>
    );

    // =========================
    // RENDER: DataSourceSelector
    // =========================

    const renderDataSourceSelector = () => {
        const groups = groupSourcesByType();

        return (
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>Filter Knowledge Base</Typography>
                        <Typography variant="body2" sx={{ fontFamily, color: '#64748b' }}>
                            Select data sources to filter the AI's responses.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Tooltip title="Refresh sources">
                            <IconButton onClick={fetchDataSources} disabled={loadingSources}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Privacy Toggle */}
                <Paper elevation={0} sx={{ p: 1.5, px: 2, mb: 2, borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon sx={{ fontSize: 20, color: personalizePrivacy ? GREEN_ACCENT : '#94a3b8' }} />
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Privacy Protection</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                {personalizePrivacy ? 'Enabled - PII will be masked' : 'Disabled - Data shown as-is'}
                            </Typography>
                        </Box>
                    </Box>
                    <Switch checked={personalizePrivacy} onChange={(e) => setPersonalizePrivacy(e.target.checked)} size="small"
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: GREEN_ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GREEN_ACCENT } }}
                    />
                </Paper>

                {sourcesError && <Alert severity="error" sx={{ mb: 2, fontFamily }} onClose={() => setSourcesError('')}>{sourcesError}</Alert>}

                {loadingSources ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>
                ) : dataSources.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '12px', bgcolor: '#f8fafc' }}>
                        <TuneIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No data sources available.</Typography>
                    </Paper>
                ) : (
                    <>
                        {Object.entries(groups).map(([type, sources]) => {
                            if (sources.length === 0) return null;
                            const config = sourceTypeConfig[type] || { label: type, icon: <DescriptionIcon /> };
                            const trainedSources = sources.filter(s => s.is_trained);
                            const typeKeys = trainedSources.map(s => getSourceKey(s.type, s.id));
                            const allSelected = typeKeys.length > 0 && typeKeys.every(k => selectedSources.includes(k));

                            return (
                                <Paper key={type} elevation={0} sx={{ mb: 2, border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, px: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ color: GREEN_ACCENT }}>{config.icon}</Box>
                                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>{config.label}</Typography>
                                            <Chip label={`${trainedSources.length} trained`} size="small" sx={{ fontFamily, bgcolor: GREEN_LIGHT, color: GREEN_DARK, fontWeight: 600, height: 22 }} />
                                        </Box>
                                        <Button size="small" onClick={() => toggleAllByType(type)}
                                            sx={{ fontFamily, fontSize: '0.75rem', textTransform: 'none', color: GREEN_ACCENT }}>
                                            {allSelected ? 'Deselect All' : 'Select All'}
                                        </Button>
                                    </Box>
                                    <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1 }}>
                                        {sources.map(source => {
                                            const key = getSourceKey(source.type, source.id);
                                            const isSelected = selectedSources.includes(key);
                                            const isTrained = source.is_trained;
                                            return (
                                                <Box
                                                    key={key}
                                                    onClick={() => isTrained && toggleSource(source.type, source.id)}
                                                    sx={{
                                                        p: 1, borderRadius: '8px', cursor: isTrained ? 'pointer' : 'not-allowed',
                                                        opacity: isTrained ? 1 : 0.5,
                                                        border: isSelected ? `2px solid ${GREEN_ACCENT}` : '1px solid #e2e8f0',
                                                        bgcolor: isSelected ? GREEN_LIGHT : '#fff',
                                                        display: 'flex', alignItems: 'center', gap: 1,
                                                        '&:hover': isTrained ? { borderColor: GREEN_ACCENT } : {},
                                                    }}
                                                >
                                                    <Checkbox checked={isSelected} size="small" disabled={!isTrained}
                                                        sx={{ p: 0, color: '#cbd5e1', '&.Mui-checked': { color: GREEN_ACCENT } }} />
                                                    <Tooltip title={source.title || 'Unnamed'}>
                                                        <Typography noWrap sx={{ fontFamily, fontSize: '0.8rem', color: '#1e293b', flex: 1 }}>
                                                            {source.title || 'Unnamed'}
                                                        </Typography>
                                                    </Tooltip>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Paper>
                            );
                        })}

                        {/* Start Chat Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Button
                                variant="contained" disabled={selectedSources.length === 0}
                                onClick={startPersonalizeChat}
                                startIcon={<PlayArrowIcon />}
                                sx={{
                                    fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                                    bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK },
                                    px: 4, py: 1.2,
                                }}
                            >
                                Start Chat ({selectedSources.length})
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        );
    };

    // =========================
    // RENDER: PersonalizeChat
    // =========================

    const renderPersonalizeChat = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <IconButton size="small" onClick={() => setIsPersonalizeConfigured(false)} sx={{ color: '#64748b' }}>
                    <ArrowBackIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <TuneIcon sx={{ color: GREEN_ACCENT }} />
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.95rem' }}>Private AI</Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                        Filtering by {selectedSources.length} source{selectedSources.length !== 1 ? 's' : ''}
                        {personalizePrivacy && ' | Privacy enabled'}
                    </Typography>
                </Box>
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, bgcolor: '#f8fafc', borderRadius: '12px', mb: 2 }}>
                {personalizeMessages.length === 0 ? renderEmptyState(
                    <TuneIcon sx={{ fontSize: 56, color: GREEN_ACCENT, opacity: 0.5 }} />,
                    'Ask about your selected data',
                    `I'll answer based on your ${selectedSources.length} selected source${selectedSources.length !== 1 ? 's' : ''}`
                ) : personalizeMessages.map((msg, idx) => renderMessageBubble(msg, idx))}
                {personalizeSending && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: GREEN_ACCENT, width: 32, height: 32 }}><SmartToyIcon sx={{ fontSize: 18 }} /></Avatar>
                        <Paper elevation={0} sx={{ p: 1.5, px: 2, borderRadius: '16px 16px 16px 4px', border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {[0, 1, 2].map(i => (
                                    <Box key={i} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#94a3b8', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: `${i * 0.16}s`, '@keyframes bounce': { '0%, 80%, 100%': { transform: 'scale(0)' }, '40%': { transform: 'scale(1)' } } }} />
                                ))}
                            </Box>
                        </Paper>
                    </Box>
                )}
                <div ref={personalizeEndRef} />
            </Box>

            {renderChatInput(personalizeInput, setPersonalizeInput, sendPersonalizeMessage, personalizeSending, 'Ask about your data...')}
        </Box>
    );

    // =========================
    // RENDER: PeopleSelector
    // =========================

    const renderPeopleSelector = () => (
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>Select Team Members</Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b' }}>
                        Include public data from your team. Your own data is always included.
                    </Typography>
                </Box>
                <Tooltip title="Refresh team"><IconButton onClick={fetchTeamMembers} disabled={loadingTeam}><RefreshIcon /></IconButton></Tooltip>
            </Box>

            {/* Privacy Toggle */}
            <Paper elevation={0} sx={{ p: 1.5, px: 2, mb: 2, borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SecurityIcon sx={{ fontSize: 20, color: peoplePrivacy ? GREEN_ACCENT : '#94a3b8' }} />
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>Privacy Protection</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>{peoplePrivacy ? 'Enabled - PII will be masked' : 'Disabled - Data shown as-is'}</Typography>
                    </Box>
                </Box>
                <Switch checked={peoplePrivacy} onChange={(e) => setPeoplePrivacy(e.target.checked)} size="small"
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: GREEN_ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GREEN_ACCENT } }}
                />
            </Paper>

            {teamError && <Alert severity="error" sx={{ mb: 2, fontFamily }} onClose={() => setTeamError('')}>{teamError}</Alert>}

            {loadingTeam ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>
            ) : teamMembers.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '12px', bgcolor: '#f8fafc' }}>
                    <PeopleIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                    <Typography sx={{ fontFamily, color: '#94a3b8' }}>No team members with public data found.</Typography>
                </Paper>
            ) : (
                <>
                    {/* Select All */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <Button size="small" onClick={() => {
                            const allIds = teamMembers.map(m => m.user_id);
                            const allSelected = allIds.every(id => selectedMembers.includes(id));
                            setSelectedMembers(allSelected ? [] : allIds);
                        }} sx={{ fontFamily, fontSize: '0.75rem', textTransform: 'none', color: GREEN_ACCENT }}>
                            {teamMembers.every(m => selectedMembers.includes(m.user_id)) ? 'Deselect All' : 'Select All'}
                        </Button>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                        {teamMembers.map(member => {
                            const isSelected = selectedMembers.includes(member.user_id);
                            const totalData = Object.values(member.data_count || {}).reduce((a, b) => a + b, 0);
                            return (
                                <Paper
                                    key={member.user_id}
                                    elevation={0}
                                    onClick={() => toggleMember(member.user_id)}
                                    sx={{
                                        p: 2, borderRadius: '12px', cursor: 'pointer',
                                        border: isSelected ? `2px solid ${GREEN_ACCENT}` : '1px solid #e2e8f0',
                                        bgcolor: isSelected ? GREEN_LIGHT : '#fff',
                                        '&:hover': { borderColor: GREEN_ACCENT, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
                                        position: 'relative',
                                    }}
                                >
                                    <Checkbox checked={isSelected} size="small" sx={{ position: 'absolute', top: 4, left: 4, p: 0.5, color: '#cbd5e1', '&.Mui-checked': { color: GREEN_ACCENT } }} />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 1 }}>
                                        <Avatar sx={{ bgcolor: isSelected ? GREEN_ACCENT : '#e2e8f0', color: isSelected ? '#fff' : '#64748b', width: 40, height: 40, fontFamily, fontWeight: 600 }}>
                                            {(member.name || '?').charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Typography noWrap sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', maxWidth: '100%' }}>
                                            {member.name || 'Unknown'}
                                        </Typography>
                                        <Chip label={member.role} size="small" sx={{ fontFamily, fontSize: '0.65rem', height: 20, bgcolor: '#f1f5f9', color: '#64748b', textTransform: 'capitalize' }} />
                                        {totalData > 0 && (
                                            <Chip label={`${totalData} items`} size="small" sx={{ fontFamily, fontSize: '0.65rem', height: 20, bgcolor: GREEN_LIGHT, color: GREEN_DARK }} />
                                        )}
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Button variant="contained" disabled={selectedMembers.length === 0} onClick={startPeopleChat} startIcon={<PlayArrowIcon />}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px', bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK }, px: 4, py: 1.2 }}>
                            Start Chat ({selectedMembers.length})
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );

    // =========================
    // RENDER: PeopleChat
    // =========================

    const renderPeopleChat = () => {
        const selectedNames = teamMembers.filter(m => selectedMembers.includes(m.user_id)).map(m => m.name);
        let dataSummary = 'Your data';
        if (selectedNames.length === 1) dataSummary += ` + ${selectedNames[0]}`;
        else if (selectedNames.length === 2) dataSummary += ` + ${selectedNames[0]} & ${selectedNames[1]}`;
        else if (selectedNames.length > 2) dataSummary += ` + ${selectedNames.length} team members`;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <IconButton size="small" onClick={() => setIsPeopleConfigured(false)} sx={{ color: '#64748b' }}>
                        <ArrowBackIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <PeopleIcon sx={{ color: GREEN_ACCENT }} />
                    <Box>
                        <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.95rem' }}>People Chat</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                            {dataSummary}{peoplePrivacy && ' | Privacy enabled'}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, bgcolor: '#f8fafc', borderRadius: '12px', mb: 2 }}>
                    {peopleMessages.length === 0 ? renderEmptyState(
                        <PeopleIcon sx={{ fontSize: 56, color: GREEN_ACCENT, opacity: 0.5 }} />,
                        "Ask about your team's knowledge",
                        `Searching through ${dataSummary}`
                    ) : peopleMessages.map((msg, idx) => renderMessageBubble(msg, idx))}
                    {peopleSending && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: GREEN_ACCENT, width: 32, height: 32 }}><SmartToyIcon sx={{ fontSize: 18 }} /></Avatar>
                            <Paper elevation={0} sx={{ p: 1.5, px: 2, borderRadius: '16px 16px 16px 4px', border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {[0, 1, 2].map(i => (
                                        <Box key={i} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#94a3b8', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: `${i * 0.16}s`, '@keyframes bounce': { '0%, 80%, 100%': { transform: 'scale(0)' }, '40%': { transform: 'scale(1)' } } }} />
                                    ))}
                                </Box>
                            </Paper>
                        </Box>
                    )}
                    <div ref={peopleEndRef} />
                </Box>

                {renderChatInput(peopleInput, setPeopleInput, sendPeopleMessage, peopleSending, "Ask about your team's data...")}
            </Box>
        );
    };

    // =========================
    // RENDER: Personalize Tab
    // =========================

    const renderPersonalizeTab = () => {
        if (!isAdminOrEmployee) {
            return <Alert severity="info" sx={{ fontFamily }}>Work Bench is available for admins and employees only.</Alert>;
        }
        return isPersonalizeConfigured ? renderPersonalizeChat() : renderDataSourceSelector();
    };

    // =========================
    // RENDER: People Tab
    // =========================

    const renderPeopleTab = () => {
        if (!isAdminOrEmployee) {
            return <Alert severity="info" sx={{ fontFamily }}>People Chat is available for admins and employees only.</Alert>;
        }
        return isPeopleConfigured ? renderPeopleChat() : renderPeopleSelector();
    };

    // =========================
    // RENDER: Citations Panel
    // =========================

    const renderCitationsPanel = () => (
        <Box sx={{ width: CITATIONS_WIDTH, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderLeft: '1px solid #e2e8f0' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArticleIcon sx={{ color: GREEN_ACCENT, fontSize: 20 }} />
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                        Sources ({citations.length})
                    </Typography>
                </Box>
                <IconButton size="small" onClick={() => { setCitations([]); setCitationsOpen(false); }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
                {citations.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <ArticleIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No sources available</Typography>
                    </Box>
                ) : (
                    citations.map((citation, idx) => {
                        const summary = citation.summary || citation.page_content || 'No summary available.';
                        const displaySummary = summary.replace(/<source_page_summary>([\s\S]*?)<\/source_page_summary>/g, '$1').trim();

                        return (
                            <Paper key={idx} elevation={0} sx={{ p: 2, mb: 1.5, borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: '#1e293b', flex: 1 }}>
                                        {citation.title_tag || `Source ${idx + 1}`}
                                    </Typography>
                                    {citation.url && (
                                        <Tooltip title="Open source">
                                            <IconButton size="small" href={citation.url} target="_blank" rel="noopener noreferrer" sx={{ color: GREEN_ACCENT, p: 0.25 }}>
                                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                                {citation.data_source && (
                                    <Chip label={citation.data_source.replace(/_/g, ' ')} size="small"
                                        sx={{ fontFamily, fontSize: '0.65rem', height: 18, mb: 1, bgcolor: '#f1f5f9', color: '#64748b', textTransform: 'capitalize' }}
                                    />
                                )}
                                <Divider sx={{ my: 1 }} />
                                <Typography sx={{
                                    fontFamily, fontSize: '0.78rem', color: '#475569', lineHeight: 1.6,
                                    display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                }}>
                                    {displaySummary}
                                </Typography>
                            </Paper>
                        );
                    })
                )}
            </Box>
        </Box>
    );

    // =========================
    // RENDER: Rating Modal
    // =========================

    const renderRatingModal = () => (
        <Dialog open={showRatingModal} onClose={() => { setShowRatingModal(false); setSessionToRateId(null); }} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
            {ratingSubmitted ? (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.3rem', color: GREEN_ACCENT, mb: 1 }}>Thank You!</Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', mb: 3 }}>Your feedback has been received.</Typography>
                    <Button variant="contained" onClick={() => { setShowRatingModal(false); setSessionToRateId(null); }}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px', bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK } }}>
                        Close
                    </Button>
                </Box>
            ) : (
                <>
                    <DialogTitle sx={{ fontFamily, fontWeight: 700, textAlign: 'center', pb: 0 }}>
                        Rate your chat{currentAgent ? ` with ${currentAgent.name}` : ''}
                    </DialogTitle>
                    <DialogContent sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 3 }}>
                            Your feedback helps us improve our support.
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Rating
                                value={ratingValue}
                                onChange={(_, newValue) => setRatingValue(newValue || 0)}
                                onChangeActive={(_, newHover) => setRatingHover(newHover)}
                                size="large"
                                sx={{ '& .MuiRating-iconFilled': { color: '#facc15' }, '& .MuiRating-iconHover': { color: '#fbbf24' } }}
                            />
                        </Box>
                        <TextField
                            fullWidth multiline rows={3}
                            placeholder="Leave an optional comment..."
                            value={ratingComment}
                            onChange={(e) => setRatingComment(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily, fontSize: '0.875rem' } }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
                        <Button onClick={() => { setShowRatingModal(false); setSessionToRateId(null); }}
                            sx={{ fontFamily, textTransform: 'none', color: '#94a3b8' }}>
                            Skip
                        </Button>
                        <Button variant="contained" onClick={handleRatingSubmit} disabled={ratingValue === 0 || ratingSubmitting}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px', bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK }, px: 4 }}>
                            {ratingSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Submit Feedback'}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );

    // =========================
    // RENDER: Feedback Modal
    // =========================

    const renderFeedbackModal = () => (
        <Dialog open={showFeedbackModal} onClose={() => { setShowFeedbackModal(false); setFeedbackMessage(null); }} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Please share your feedback
                <IconButton size="small" onClick={() => { setShowFeedbackModal(false); setFeedbackMessage(null); }}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 3 }}>
                    Help us improve our AI responses. Your feedback is valuable.
                </Typography>
                <RadioGroup value={feedbackIssue} onChange={(e) => setFeedbackIssue(e.target.value)}>
                    {['The response was not accurate', 'The response was incomplete', 'Other (please specify below)'].map((option) => (
                        <FormControlLabel key={option} value={option} control={<Radio size="small" sx={{ '&.Mui-checked': { color: GREEN_ACCENT } }} />}
                            label={<Typography sx={{ fontFamily, fontSize: '0.875rem' }}>{option}</Typography>}
                            sx={{ mb: 0.5 }}
                        />
                    ))}
                </RadioGroup>
                <TextField
                    fullWidth multiline rows={3}
                    placeholder="Your comments..."
                    value={feedbackComments}
                    onChange={(e) => setFeedbackComments(e.target.value)}
                    sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily, fontSize: '0.875rem' } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button variant="contained" onClick={handleFeedbackSubmit} disabled={!feedbackIssue}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px', bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK } }}>
                    Submit Feedback
                </Button>
            </DialogActions>
        </Dialog>
    );

    // =========================
    // TAB CONFIG
    // =========================

    const tabs = [
        { label: 'General', icon: <ChatIcon sx={{ fontSize: 18 }} /> },
    ];
    if (isAdminOrEmployee) {
        tabs.push(
            { label: 'Work Bench', icon: <TuneIcon sx={{ fontSize: 18 }} /> },
            { label: 'People', icon: <PeopleIcon sx={{ fontSize: 18 }} /> }
        );
    }

    // =========================
    // MAIN RENDER
    // =========================

    return (
        <Box sx={{ height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {chatbotConfigured === null ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}><CircularProgress sx={{ color: GREEN_ACCENT }} /></Box>
            ) : chatbotConfigured === false ? (
                <Box sx={{ p: 3 }}>
                    <Paper elevation={0} sx={{ p: 4, borderRadius: '16px', border: '1px solid #fbbf24', bgcolor: '#fffbeb', textAlign: 'center' }}>
                        <WarningAmberIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#92400e', mb: 1 }}>Chatbot Configuration Required</Typography>
                        <Typography variant="body2" sx={{ fontFamily, color: '#78350f', mb: 2 }}>
                            {isAdminOrEmployee ? 'Please go to Settings to configure the chatbot.' : 'Please contact your administrator to set up the chatbot.'}
                        </Typography>
                        {isAdminOrEmployee && (
                            <Button variant="contained" onClick={() => navigate('/customer-panel/settings')}
                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' }, borderRadius: '10px' }}>
                                Go to Settings
                            </Button>
                        )}
                    </Paper>
                </Box>
            ) : (
                <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                    {/* Left Sidebar - Desktop */}
                    <Box sx={{ display: { xs: 'none', lg: 'flex' } }}>
                        {renderSidebar()}
                    </Box>

                    {/* Left Sidebar - Mobile Drawer */}
                    <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)}
                        PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}>
                        {renderSidebar()}
                    </Drawer>

                    {/* Main Content */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
                        {/* Top Bar */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                            <IconButton sx={{ display: { lg: 'none' } }} size="small" onClick={() => setSidebarOpen(true)}>
                                <MenuIcon />
                            </IconButton>
                            <Tabs
                                value={activeTab}
                                onChange={(_, v) => setActiveTab(v)}
                                sx={{
                                    flex: 1, minHeight: 40,
                                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem', minHeight: 40, py: 0 },
                                    '& .Mui-selected': { color: `${GREEN_ACCENT} !important` },
                                    '& .MuiTabs-indicator': { bgcolor: GREEN_ACCENT },
                                }}
                            >
                                {tabs.map((tab, idx) => (
                                    <Tab key={idx} label={tab.label} icon={tab.icon} iconPosition="start" />
                                ))}
                            </Tabs>
                            {citations.length > 0 && (
                                <Tooltip title="Toggle Sources">
                                    <IconButton size="small" onClick={() => setCitationsOpen(!citationsOpen)}
                                        sx={{ display: { lg: 'none' }, color: GREEN_ACCENT }}>
                                        <Badge badgeContent={citations.length} color="success">
                                            <ArticleIcon />
                                        </Badge>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>

                        {/* Tab Content */}
                        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                            {activeTab === 0 && renderGeneralTab()}
                            {activeTab === 1 && isAdminOrEmployee && renderPersonalizeTab()}
                            {activeTab === 2 && isAdminOrEmployee && renderPeopleTab()}
                        </Box>
                    </Box>

                    {/* Right Citations - Desktop */}
                    {citations.length > 0 && (
                        <Box sx={{ display: { xs: 'none', lg: 'flex' } }}>
                            {renderCitationsPanel()}
                        </Box>
                    )}

                    {/* Right Citations - Mobile Drawer */}
                    <Drawer anchor="right" open={citationsOpen && citations.length > 0} onClose={() => setCitationsOpen(false)}
                        PaperProps={{ sx: { width: CITATIONS_WIDTH } }}
                        sx={{ display: { lg: 'none' } }}>
                        {renderCitationsPanel()}
                    </Drawer>
                </Box>
            )}

            {/* Modals */}
            {renderRatingModal()}
            {renderFeedbackModal()}
        </Box>
    );
}
